/**
 * Analytics Hooks
 *
 * React Query hooks for fetching user activity analytics from the USER_ACTIVITY table
 */

import { useQuery } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

// Types
export interface ActivityVisit {
  PK: string
  SK: string
  userId: string
  userName: string
  area: string
  entityId?: string
  entityName?: string
  path: string
  duration: number
  sessionId: string
  timestamp: number
  createdAt: string
}

export interface AreaBreakdownItem {
  area: string
  duration: number
  durationFormatted: string
  visits?: number
  uniqueUsers?: number
}

export interface UserBreakdownItem {
  userId: string
  userName: string
  duration: number
  durationFormatted: string
  visits: number
  areasVisited?: number
}

export interface EntityVisitor {
  userId: string
  userName: string
}

export interface EntityBreakdownItem {
  entityId: string
  entityName: string
  area: string
  duration: number
  durationFormatted: string
  visits: number
  uniqueUsers: number
  visitors: EntityVisitor[]
}

export interface TaskStatusBreakdownItem {
  status: string
  duration: number
  durationFormatted: string
}

export interface OrganizationTimeItem {
  orgId: string
  directTime: number
  directTimeFormatted: string
  dealTime: number
  dealTimeFormatted: string
  taskTime: number
  taskTimeFormatted: string
  totalTime: number
  totalTimeFormatted: string
  visits: number
  dealVisits: number
  taskVisits: number
  uniqueUsers: number
  visitors: EntityVisitor[]
  relatedDealsViewed: number
  relatedTasksViewed: number
  taskStatusBreakdown: TaskStatusBreakdownItem[]
}

export interface OrganizationTimeResponse {
  timeRange: {
    start: number
    end: number
    startFormatted: string
    endFormatted: string
  }
  organizationTime: OrganizationTimeItem[]
  totalOrgsWithActivity: number
}

export interface UserActivityResponse {
  userId: string
  visits: ActivityVisit[]
  summary: {
    totalVisits: number
    totalDuration: number
    totalDurationFormatted: string
    areaBreakdown: AreaBreakdownItem[]
  }
}

export interface AreaActivityResponse {
  area: string
  visits: ActivityVisit[]
  summary: {
    totalVisits: number
    totalDuration: number
    totalDurationFormatted: string
    uniqueUsers: number
    userBreakdown: UserBreakdownItem[]
  }
}

export interface ActivitySummaryResponse {
  timeRange: {
    start: number
    end: number
    startFormatted: string
    endFormatted: string
  }
  summary: {
    totalVisits: number
    totalDuration: number
    totalDurationFormatted: string
    uniqueUsers: number
    averageDurationPerVisit: number
    averageDurationPerVisitFormatted: string
  }
  areaBreakdown: AreaBreakdownItem[]
  userBreakdown: UserBreakdownItem[]
  topEntities: EntityBreakdownItem[]
}

export interface RecentActivityItem {
  userId: string
  userName: string
  area: string
  entityName?: string
  path: string
  duration: number
  durationFormatted: string
  timestamp: number
  timeAgo: string
}

export interface RecentActivityResponse {
  recentActivity: RecentActivityItem[]
}

// API Functions
const fetchUserActivity = async (
  userId: string,
  options?: { startTime?: number; endTime?: number; limit?: number }
): Promise<UserActivityResponse> => {
  const params = new URLSearchParams()
  if (options?.startTime) params.append('startTime', String(options.startTime))
  if (options?.endTime) params.append('endTime', String(options.endTime))
  if (options?.limit) params.append('limit', String(options.limit))

  const url = `${baseUrl}/aws/analytics/user/${userId}${params.toString() ? `?${params}` : ''}`
  const { data } = await axios.get<UserActivityResponse>(url)
  return data
}

const fetchAreaActivity = async (
  area: string,
  options?: { startTime?: number; endTime?: number; limit?: number }
): Promise<AreaActivityResponse> => {
  const params = new URLSearchParams()
  if (options?.startTime) params.append('startTime', String(options.startTime))
  if (options?.endTime) params.append('endTime', String(options.endTime))
  if (options?.limit) params.append('limit', String(options.limit))

  const url = `${baseUrl}/aws/analytics/area/${encodeURIComponent(area)}${params.toString() ? `?${params}` : ''}`
  const { data } = await axios.get<AreaActivityResponse>(url)
  return data
}

const fetchActivitySummary = async (options?: {
  startTime?: number
  endTime?: number
}): Promise<ActivitySummaryResponse> => {
  const params = new URLSearchParams()
  if (options?.startTime) params.append('startTime', String(options.startTime))
  if (options?.endTime) params.append('endTime', String(options.endTime))

  const url = `${baseUrl}/aws/analytics/summary${params.toString() ? `?${params}` : ''}`
  const { data } = await axios.get<ActivitySummaryResponse>(url)
  return data
}

const fetchRecentActivity = async (limit?: number): Promise<RecentActivityResponse> => {
  const url = `${baseUrl}/aws/analytics/recent${limit ? `?limit=${limit}` : ''}`
  const { data } = await axios.get<RecentActivityResponse>(url)
  return data
}

const fetchOrganizationTime = async (
  orgDealMappings: Record<string, string[]>,
  options?: { startTime?: number; endTime?: number }
): Promise<OrganizationTimeResponse> => {
  const url = `${baseUrl}/aws/analytics/organization-time`
  const { data } = await axios.post<OrganizationTimeResponse>(url, {
    orgDealMappings,
    startTime: options?.startTime,
    endTime: options?.endTime,
  })
  return data
}

// Hooks

/**
 * Fetch activity for a specific user
 */
export const useUserActivity = (
  userId: string | undefined,
  options?: { startTime?: number; endTime?: number; limit?: number; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['analytics', 'user', userId, options?.startTime, options?.endTime, options?.limit],
    queryFn: () => fetchUserActivity(userId!, options),
    enabled: !!userId && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Fetch activity for a specific area
 */
export const useAreaActivity = (
  area: string | undefined,
  options?: { startTime?: number; endTime?: number; limit?: number; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['analytics', 'area', area, options?.startTime, options?.endTime, options?.limit],
    queryFn: () => fetchAreaActivity(area!, options),
    enabled: !!area && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Fetch overall activity summary
 */
export const useActivitySummary = (options?: { startTime?: number; endTime?: number; enabled?: boolean }) => {
  return useQuery({
    queryKey: ['analytics', 'summary', options?.startTime, options?.endTime],
    queryFn: () => fetchActivitySummary(options),
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Fetch recent activity across all users
 */
export const useRecentActivity = (limit?: number, enabled = true) => {
  return useQuery({
    queryKey: ['analytics', 'recent', limit],
    queryFn: () => fetchRecentActivity(limit),
    enabled,
    staleTime: 1000 * 30, // 30 seconds - more frequent refresh for real-time feel
    refetchInterval: 1000 * 60, // Auto-refresh every minute
  })
}

/**
 * Fetch organization time aggregation
 * Aggregates time spent on org pages + related deals + related tasks
 *
 * @param orgDealMappings - Object mapping org IDs to arrays of their deal IDs
 * @param options - Time range and enabled flag
 */
export const useOrganizationTime = (
  orgDealMappings: Record<string, string[]> | undefined,
  options?: { startTime?: number; endTime?: number; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['analytics', 'organization-time', orgDealMappings, options?.startTime, options?.endTime],
    queryFn: () => fetchOrganizationTime(orgDealMappings!, options),
    enabled: !!orgDealMappings && Object.keys(orgDealMappings).length > 0 && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Google Ads Activity Types (from Chrome extension tracking)
export interface GoogleAdsSession {
  PK: string
  SK: string
  session_uuid: string
  userId: string
  platform: 'google_ads' | 'meta_ads' | 'linkedin_ads' | 'other'
  startTime: string
  endTime: string
  durationMinutes: number
  durationMs: number
  context: {
    accountId?: string | null
    campaignId?: string | null
    page?: string | null
  }
  url: string
  createdAt: string
  type: 'ACTIVITY_SESSION'
}

export interface GoogleAdsActivityResponse {
  userId: string
  sessions: GoogleAdsSession[]
  summary: {
    totalSessions: number
    totalMinutes: number
    totalHours: number
    byPlatform: Record<string, number>
  }
}

export interface GoogleAdsTeamSummaryUser {
  userId: string
  sessions: number
  totalMinutes: number
  totalHours: number
  byPlatform: Record<string, number>
}

export interface GoogleAdsTeamSummaryResponse {
  users: GoogleAdsTeamSummaryUser[]
  team: {
    totalMinutes: number
    totalSessions: number
    totalHours: number
    byPlatform: Record<string, number>
  }
  dateRange: {
    start: string
    end: string
  }
}

// Fetch Google Ads activity sessions for a user
const fetchGoogleAdsActivity = async (
  userId: string,
  options?: { startDate?: string; endDate?: string; platform?: string }
): Promise<GoogleAdsActivityResponse> => {
  const params = new URLSearchParams()
  if (options?.startDate) params.append('startDate', options.startDate)
  if (options?.endDate) params.append('endDate', options.endDate)
  if (options?.platform) params.append('platform', options.platform)

  const url = `${baseUrl}/api/activity-tracker/sessions/${userId}${params.toString() ? `?${params}` : ''}`
  const { data } = await axios.get<GoogleAdsActivityResponse>(url)
  return data
}

// Fetch Google Ads team summary
const fetchGoogleAdsTeamSummary = async (
  userIds: string[],
  options?: { startDate?: string; endDate?: string }
): Promise<GoogleAdsTeamSummaryResponse> => {
  const params = new URLSearchParams()
  params.append('userIds', userIds.join(','))
  if (options?.startDate) params.append('startDate', options.startDate)
  if (options?.endDate) params.append('endDate', options.endDate)

  const url = `${baseUrl}/api/activity-tracker/team-summary?${params}`
  const { data } = await axios.get<GoogleAdsTeamSummaryResponse>(url)
  return data
}

/**
 * Fetch Google Ads activity for a specific user
 */
export const useGoogleAdsActivity = (
  userId: string | undefined,
  options?: { startDate?: string; endDate?: string; platform?: string; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['google-ads-activity', 'user', userId, options?.startDate, options?.endDate, options?.platform],
    queryFn: () => fetchGoogleAdsActivity(userId!, options),
    enabled: !!userId && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Fetch Google Ads team summary for multiple users
 */
export const useGoogleAdsTeamSummary = (
  userIds: string[] | undefined,
  options?: { startDate?: string; endDate?: string; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['google-ads-activity', 'team', userIds, options?.startDate, options?.endDate],
    queryFn: () => fetchGoogleAdsTeamSummary(userIds!, options),
    enabled: !!userIds && userIds.length > 0 && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Types for by-account aggregation
export interface GoogleAdsAccountBreakdown {
  accountId: string
  totalMinutes: number
  totalHours: number
  sessions: number
  uniqueUsers: number
  userIds: string[]
  orgId: string | null
  orgName: string | null
}

export interface GoogleAdsByAccountResponse {
  accounts: GoogleAdsAccountBreakdown[]
  totals: {
    totalMinutes: number
    totalHours: number
    totalSessions: number
    accountsTracked: number
    accountsWithOrg: number
  }
  dateRange: {
    start: string
    end: string
  }
}

// Fetch Google Ads time aggregated by account ID
const fetchGoogleAdsByAccount = async (
  userIds: string[],
  accountOrgMapping: Record<string, { orgId: string; orgName: string }>,
  options?: { startDate?: string; endDate?: string }
): Promise<GoogleAdsByAccountResponse> => {
  const url = `${baseUrl}/api/activity-tracker/by-account`
  const { data } = await axios.post<GoogleAdsByAccountResponse>(url, {
    userIds,
    accountOrgMapping,
    startDate: options?.startDate,
    endDate: options?.endDate,
  })
  return data
}

/**
 * Fetch Google Ads time aggregated by account ID with org mapping
 * @param userIds - Array of user IDs to include
 * @param accountOrgMapping - Maps Google Ads account IDs to organization info
 */
export const useGoogleAdsByAccount = (
  userIds: string[] | undefined,
  accountOrgMapping: Record<string, { orgId: string; orgName: string }> | undefined,
  options?: { startDate?: string; endDate?: string; enabled?: boolean }
) => {
  return useQuery({
    queryKey: ['google-ads-activity', 'by-account', userIds, accountOrgMapping, options?.startDate, options?.endDate],
    queryFn: () => fetchGoogleAdsByAccount(userIds!, accountOrgMapping || {}, options),
    enabled: !!userIds && userIds.length > 0 && options?.enabled !== false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

// Utility: Time range presets
export const getTimeRangePreset = (preset: 'today' | 'yesterday' | 'week' | 'month' | 'custom') => {
  const now = Date.now()
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  switch (preset) {
    case 'today':
      return { startTime: today.getTime(), endTime: now }
    case 'yesterday': {
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      return { startTime: yesterday.getTime(), endTime: today.getTime() }
    }
    case 'week': {
      const weekAgo = new Date(today)
      weekAgo.setDate(weekAgo.getDate() - 7)
      return { startTime: weekAgo.getTime(), endTime: now }
    }
    case 'month': {
      const monthAgo = new Date(today)
      monthAgo.setMonth(monthAgo.getMonth() - 1)
      return { startTime: monthAgo.getTime(), endTime: now }
    }
    default:
      return { startTime: today.getTime(), endTime: now }
  }
}
